import { type CommonBullJobResponseDto, MediaService } from 'app/API';
import { cancelOnSignal } from 'app/API/core/CancelablePromise';
import type { QueryFunctionContext } from '@tanstack/react-query';
import { isRetryableAnalysisPollError } from '@helpers/mediaAnalysis/isRetryableAnalysisPollError';
import type { TCall } from 'app/queries/types/TCall';
import { collectMediaAnalysisRecords, type TMediaAnalysisRecord } from './collectMediaAnalysisRecords';
import type { mediaKeys } from './mediaKeys';

export type TMediaAnalysisProgress = TMediaAnalysisRecord & {
  legacyJob?: Partial<CommonBullJobResponseDto>;
};

export type TMediaAnalysisPoll = {
  recordsByMediaId?: Record<string, TMediaAnalysisRecord>;
  consecutiveFailures: number;
};

type TParams = QueryFunctionContext<ReturnType<typeof mediaKeys.analysisProgress>>;

export const createFetchMediaAnalysisProgressQueryFn =
  (call: TCall) =>
  async ({ client, queryKey, signal }: TParams): Promise<TMediaAnalysisPoll> => {
    const [{ jobId }] = queryKey;
    try {
      const { media } = await call(() => cancelOnSignal(MediaService.getFilesList({ jobId }), signal), {
        dontRedirectOnError: true,
      });
      return {
        recordsByMediaId: collectMediaAnalysisRecords(media ?? []),
        consecutiveFailures: 0,
      };
    } catch (error) {
      if (signal.aborted || !isRetryableAnalysisPollError(error)) {
        throw error;
      }
      const previous = client.getQueryData<TMediaAnalysisPoll>(queryKey);
      return {
        recordsByMediaId: previous?.recordsByMediaId,
        consecutiveFailures: (previous?.consecutiveFailures ?? 0) + 1,
      };
    }
  };
